import React from 'react';
import { GameEvent, GameState, ZoneTriggeredGameEvent } from '../../types/game';
import { computeScore } from '../../domain/scoring';
import { gameRules } from '../../domain/gameRules';
import { WIN_SCORE } from '../../utils/constants';
import { zoneChainLinks } from '../../domain/zoneNarrative';
import { zoneStageTiming } from '../../utils/zoneStageTiming';
import { isTunnelScore } from '../../utils/soundMap';
import { canRescueByFive } from '../../domain/dice';
import { useSound } from '../SoundContext';

interface Params {
  gameState: GameState;
  replayExistingEvents?: boolean;
}

const getPayload = (event: GameEvent): Record<string, unknown> => {
  const payload = event.payload;
  if (typeof payload === 'object' && payload !== null) {
    return payload as unknown as Record<string, unknown>;
  }
  return {};
};

const getPayloadField = (event: GameEvent, key: string): unknown => {
  const payload = getPayload(event);
  return payload[key];
};

const safeDelta = (event: GameEvent): number => {
  const candidate = getPayloadField(event, 'delta');
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : 0;
};

const safeDiceFromEvent = (event: GameEvent): { dice: number[]; keptMask: boolean[]; rollIndex: number } => {
  const payload = getPayload(event);
  const rawDice = payload.dice;
  const rawKeptMask = payload.keptMask;
  const dice = Array.isArray(rawDice) ? rawDice.filter((die): die is number => typeof die === 'number' && Number.isFinite(die)) : [];
  const keptMask = Array.isArray(rawKeptMask) ? rawKeptMask.filter((flag): flag is boolean => typeof flag === 'boolean') : [];
  const rollIndexRaw = payload.rollIndex;
  const rollIndexParsed = typeof rollIndexRaw === 'number' ? rollIndexRaw : Number(rollIndexRaw);
  const rollIndex = Number.isFinite(rollIndexParsed) ? rollIndexParsed : -1;
  return { dice, keptMask, rollIndex };
};

const safeResultingScore = (event: GameEvent): number | null => {
  const candidate = getPayloadField(event, 'resultingScore');
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

const safePlayerIdFromEvent = (event: GameEvent): string | null => {
  const candidate = getPayloadField(event, 'playerId');
  return typeof candidate === 'string' ? candidate : null;
};

const isFullRoll = (keptMask: boolean[]): boolean => {
  if (!Array.isArray(keptMask) || keptMask.length === 0) return true;
  return keptMask.every((flag) => !flag);
};

const isStraight = (dice: number[]): boolean => {
  if (!Array.isArray(dice) || dice.length !== 5) return false;
  const sorted = [...dice].sort((a, b) => a - b);
  const pattern1 = [1, 2, 3, 4, 5];
  const pattern2 = [2, 3, 4, 5, 6];
  return sorted.every((value, idx) => value === pattern1[idx])
    || sorted.every((value, idx) => value === pattern2[idx]);
};

const allKept = (keptMask: boolean[]): boolean => {
  if (!Array.isArray(keptMask) || keptMask.length === 0) return false;
  return keptMask.every(Boolean);
};

export function useSoundEffects({ gameState, replayExistingEvents = false }: Params) {
  const { playSound } = useSound();
  const processedRef = React.useRef<Set<string>>(new Set());
  const primedRef = React.useRef(false);
  const victoryPlayedRef = React.useRef(false);
  const firstRollFuryKeyRef = React.useRef<Set<string>>(new Set());
  const firstRollFuryTurnRef = React.useRef<Map<string, number>>(new Map());
  const straightRolledRef = React.useRef<Map<string, boolean>>(new Map());
  const straightScoredRef = React.useRef<Map<string, boolean>>(new Map());
  const turnMetaRef = React.useRef<Map<string, { lastFullStraightRoll: boolean; keptMask: boolean[] }>>(new Map());
  const gameStateRef = React.useRef<GameState>(gameState);
  const prevGameOverRef = React.useRef(gameState.isGameOver);
  const zoneSoundTimersRef = React.useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  React.useEffect(() => () => {
    zoneSoundTimersRef.current.forEach((timer) => clearTimeout(timer));
    zoneSoundTimersRef.current.clear();
  }, []);

  React.useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  const triggerVictory = React.useCallback(() => {
    if (victoryPlayedRef.current) return;
    victoryPlayedRef.current = true;
    playSound('victory');
  }, [playSound]);

  const handleEvent = React.useCallback((event: GameEvent) => {
    const currentState = gameStateRef.current;
    switch (event.type) {
      case 'DICE_ROLL': {
        const { dice, keptMask, rollIndex } = safeDiceFromEvent(event);
        const playerId = safePlayerIdFromEvent(event);
        const eventTurn = Number(event.turn);
        const firstRollKey = `${Number.isFinite(eventTurn) ? eventTurn : 'na'}:${playerId || 'unknown'}`;
        const straightKey = firstRollKey;
        const rolledDice = dice.filter((_, idx) => !keptMask[idx]);
        playSound('diceRoll');
        if (playerId && Number.isFinite(eventTurn)) {
          const prevMeta = turnMetaRef.current.get(straightKey);
          if (prevMeta?.lastFullStraightRoll && allKept(prevMeta.keptMask)) {
            straightScoredRef.current.set(straightKey, true);
          }
          const fullStraightRoll = Array.isArray(dice) && isFullRoll(keptMask) && isStraight(dice);
          if (fullStraightRoll) {
            straightRolledRef.current.set(straightKey, true);
          }
          turnMetaRef.current.set(straightKey, { lastFullStraightRoll: fullStraightRoll, keptMask });
        }
        if (rolledDice.length > 0) {
          const scoring = computeScore(rolledDice);
          const isBust = (scoring.scoringDiceCount || 0) === 0;
          if (isBust) {
            const rescueAvailable = currentState.settings.rescueByFive && canRescueByFive(rolledDice, scoring.scoringDiceCount || 0);
            if (rescueAvailable) {
              playSound('rescueByFive');
            } else {
              const isFirstRoll = rollIndex === 0;
              if (isFirstRoll) {
                if (!firstRollFuryKeyRef.current.has(firstRollKey)) {
                  firstRollFuryKeyRef.current.add(firstRollKey);
                  if (playerId && Number.isFinite(eventTurn)) {
                    firstRollFuryTurnRef.current.set(playerId, eventTurn);
                  }
                  playSound('furyFirstRoll');
                }
              } else {
                playSound('fury');
              }
            }
          }
        }
        break;
      }
      case 'DICE_KEEP': {
        const keptMask = Array.isArray(event.payload.keptMask)
          ? event.payload.keptMask.filter((flag): flag is boolean => typeof flag === 'boolean')
          : [];
        const playerId = safePlayerIdFromEvent(event);
        const eventTurn = Number(event.turn);
        const straightKey = `${Number.isFinite(eventTurn) ? eventTurn : 'na'}:${playerId || 'unknown'}`;
        if (playerId && Number.isFinite(eventTurn)) {
          const prevMeta = turnMetaRef.current.get(straightKey);
          turnMetaRef.current.set(straightKey, {
            lastFullStraightRoll: prevMeta?.lastFullStraightRoll ?? false,
            keptMask,
          });
        }
        break;
      }
      case 'TURN_END': {
        const basePoints = Number(event.payload.basePoints || 0);
        const overshotWinScore = getPayloadField(event, 'overshotWinScore') === true;
        const playerId = event.payload.playerId;
        const eventTurn = Number(event.turn);
        const straightKey = `${Number.isFinite(eventTurn) ? eventTurn : 'na'}:${playerId || 'unknown'}`;
        const meta = turnMetaRef.current.get(straightKey);
        if (meta?.lastFullStraightRoll && allKept(meta.keptMask)) {
          straightScoredRef.current.set(straightKey, true);
        }
        const hadStraight = straightRolledRef.current.get(straightKey) === true;
        const scoredStraight = straightScoredRef.current.get(straightKey) === true;
        straightRolledRef.current.delete(straightKey);
        straightScoredRef.current.delete(straightKey);
        turnMetaRef.current.delete(straightKey);
        if (hadStraight && !scoredStraight) {
          playSound('missedStraight');
        }
        if (overshotWinScore) {
          playSound('overThousand');
        }
        if (!basePoints) break;
        const player = (currentState.players || []).find((p) => p.id === playerId);
        const currentScore = player?.score || 0;
        const projected = currentScore + basePoints;
        playSound('scoreSave');
        if (isTunnelScore(projected)) {
          playSound('tunnelStop');
        }
        break;
      }
      case 'SCORE': {
        const resultingScore = safeResultingScore(event);
        const delta = safeDelta(event);
        const isWinning = resultingScore !== null && resultingScore === WIN_SCORE;
        if (delta > 0 && !isWinning) {
          playSound('scoreSave');
          if (resultingScore !== null && isTunnelScore(resultingScore)) {
            playSound('tunnelStop');
          }
        }
        if (isWinning) {
          triggerVictory();
        }
        break;
      }
      case 'ZONE_TRIGGERED': {
        // Dźwięk zgrany z inscenizacją na racetracku: każde ogniwo łańcucha dostaje własny
        // sygnał w momencie, w którym marker na nie dojeżdża i strefa się odsłania.
        // Harmonogram bierzemy z tego samego źródła co animacja, żeby obraz i dźwięk
        // nie mogły się rozjechać.
        const links = zoneChainLinks((event as ZoneTriggeredGameEvent).payload);
        const { arrivals } = zoneStageTiming(links.length);
        links.forEach((link, index) => {
          const timer = setTimeout(
            () => playSound(link.kind === 'penalty' ? 'zonePenalty' : 'zoneBonus'),
            arrivals[index],
          );
          zoneSoundTimersRef.current.add(timer);
        });
        break;
      }
      case 'PENALTY': {
        const eventTurn = Number(event.turn);
        const playerId = safePlayerIdFromEvent(event);
        const firstRollKey = `${Number.isFinite(eventTurn) ? eventTurn : 'na'}:${playerId || 'unknown'}`;
        const resultingScore = safeResultingScore(event);
        const delta = safeDelta(event);
        const reason = getPayloadField(event, 'reason');
        const isFirstFuryPenalty = reason === 'PIERWSZA_FURA' && delta === gameRules.penalties.firstRollBust;
        if (isFirstFuryPenalty) {
          const lastPlayedTurn = playerId ? firstRollFuryTurnRef.current.get(playerId) : undefined;
          const hasRecentRollFury = typeof lastPlayedTurn === 'number'
            && (Number.isFinite(eventTurn)
              ? (lastPlayedTurn === eventTurn || lastPlayedTurn === eventTurn - 1)
              : true);
          if (!firstRollFuryKeyRef.current.has(firstRollKey) && !hasRecentRollFury) {
            firstRollFuryKeyRef.current.add(firstRollKey);
            if (playerId && Number.isFinite(eventTurn)) {
              firstRollFuryTurnRef.current.set(playerId, eventTurn);
            }
            playSound('furyFirstRoll');
          }
        }
        if (resultingScore !== null && isTunnelScore(resultingScore)) {
          playSound('tunnelStop');
        }
        break;
      }
      default:
        break;
    }
  }, [playSound, triggerVictory]);

  React.useEffect(() => {
    // Prime processed set on first render per gameId to avoid replaying historic events.
    if (!primedRef.current) {
      if (!replayExistingEvents) {
        (gameState.events || []).forEach((event) => {
          if (event?.id) {
            processedRef.current.add(event.id);
          }
        });
        primedRef.current = true;
        return;
      }
      primedRef.current = true;
    }
    (gameState.events || []).forEach((event) => {
      if (!event?.id) return;
      if (processedRef.current.has(event.id)) return;
      processedRef.current.add(event.id);
      handleEvent(event);
    });
  }, [gameState.events, handleEvent, replayExistingEvents]);

  React.useEffect(() => {
    const wasGameOver = prevGameOverRef.current;
    prevGameOverRef.current = gameState.isGameOver;
    if (!wasGameOver && gameState.isGameOver) {
      triggerVictory();
      return;
    }
    if (!gameState.isGameOver) {
      victoryPlayedRef.current = false;
    }
  }, [gameState.isGameOver, triggerVictory]);

  React.useEffect(() => {
    processedRef.current = new Set();
    primedRef.current = false;
    victoryPlayedRef.current = false;
    firstRollFuryKeyRef.current = new Set();
    firstRollFuryTurnRef.current = new Map();
    straightRolledRef.current = new Map();
    straightScoredRef.current = new Map();
    turnMetaRef.current = new Map();
    prevGameOverRef.current = gameState.isGameOver;
  }, [gameState.id, gameState.isGameOver]);
}
